import React, {PureComponent} from 'react';
import styled from 'styled-components';
import {v4 as uuid} from 'uuid';

import {filterCatalog, buildCatalogTree, buildCatalogMap, getFriendlyCatalogPathName, getParents} from '../../lib/catalog';
import Input from '../Input';
import Card from '../Card';
import Popover from '../Popover';
import Button from '../Button';

class CatalogInput extends PureComponent {
  id = uuid();
  searchRef = React.createRef();
  state = {
    showCatalog: false,
    search: ""
  };

  componentDidMount() {
    //opened straight away when the answer is being edited, so typing goes into the search
    if (this.props.autoOpen) {
      this.setState({showCatalog: true});
    }
  }

  //the search takes the focus whenever the catalog opens, without scrolling to it before the popover is positioned
  componentDidUpdate(prevProps, prevState) {
    if (!prevState.showCatalog && this.state.showCatalog && this.searchRef.current) {
      this.searchRef.current.focus({preventScroll: true});
    }
  }

  handleFocus = () => {
    this.setState({showCatalog: true});
  }

  handleBlur = () => {
    this.setState({showCatalog: false, search: ""});
  }

  handleChange = itemId => {
    const {value, onChange, multiple} = this.props;
    
    if (multiple) {
      if (value.indexOf(itemId) >= 0) {
        onChange(value.filter(v => v !== itemId));
      }
      else {
        onChange([...value, itemId]);
      }
    }
    else {
      this.handleBlur();
      onChange(itemId);
    }
  }

  getId() {
    return this.props.id ? this.props.id : this.id;
  }

  render() {
    const {catalog, value, multiple, selectableTypeIds, leafOnly, autoOpen, ...inputProps} = this.props;
    const {showCatalog, search} = this.state;
    const catalogById = buildCatalogMap(catalog);
    const selectable = getSelectableItemIds(catalog, selectableTypeIds, leafOnly, value);
    const matching = filterCatalog(search, selectable ? withParents(selectable, catalogById) : catalogById);
    const tree = buildCatalogTree(matching);
    let friendlyValue = '';

    if (multiple && !Array.isArray(value)) {
      throw new Error('The multiple variant of CatalogInput must have an array value');
    }

    if (multiple) {
      friendlyValue = value
        .map(id => catalogById[id.trim()])
        .filter(item => !!item)
        .map(item => item.name)
        .join(', ');
    }
    else if (value) {
      friendlyValue = getFriendlyCatalogPathName(catalogById[value.trim()], catalogById);
    }

    return (
      <>
        <Input
          {...inputProps}
          readOnly
          id={this.getId()}
          type="text"
          value={friendlyValue}
          onFocus={this.handleFocus}
        />
        <Popover fullWidth open={showCatalog} relativeTo={this.getId()} onClose={this.handleBlur}>
          <Input ref={this.searchRef} type={"text"} placeholder={"Search catalog"} value={search} onChange={e => this.setState({search: e.target.value})}/>
          <CatalogContainer>
            {tree.length > 0 && restrictsTypes(selectableTypeIds) ? (
              groupByType(tree).map(group => (
                <React.Fragment key={group.type.id}>
                  <TypeHeader>{group.type.name}</TypeHeader>
                  <CatalogTree multiple={multiple} tree={group.tree} value={value} selectable={selectable} onSelect={this.handleChange} />
                </React.Fragment>
              ))
            ) : tree.length > 0 ? (
              <CatalogTree multiple={multiple} tree={tree} value={value} selectable={selectable} onSelect={this.handleChange} />
            ) : (
              <EmptyMessage>
                {search ? 'No catalog items match your search' : 'No catalog items can be picked for this question'}
              </EmptyMessage>
            )}
          </CatalogContainer>
        </Popover>
      </>
    );
  }
}

//The ids of the items that can be picked, or null when any item can. An answer can be held to the catalog types a
//question is restricted to, and to the leaf items among them (the ones with no item of those types under them), as the
//mobile app does. Items already in the answer stay pickable, so a pick the rules no longer allow can still be removed.
export function getSelectableItemIds(catalog, selectableTypeIds, leafOnly, value) {
  const restrictTypes = restrictsTypes(selectableTypeIds);

  if (!catalog || (!restrictTypes && !leafOnly)) {
    return null;
  }

  const catalogById = buildCatalogMap(catalog);
  const allowed = catalog.filter(item => !restrictTypes || (item.type && selectableTypeIds.indexOf(item.type.id) >= 0));
  //only the allowed items count as children, so an item of a restricted type with other types under it is a leaf
  const parentIds = new Set();
  allowed.forEach(item => getParents(item, catalogById).forEach(parent => parentIds.add(parent.id)));

  const selectable = new Set(allowed.filter(item => !leafOnly || !parentIds.has(item.id)).map(item => item.id));
  (Array.isArray(value) ? value : [value])
    .filter(id => id && catalogById[id.trim()])
    .forEach(id => selectable.add(id.trim()));

  return selectable;
}

function restrictsTypes(selectableTypeIds) {
  return !!selectableTypeIds && selectableTypeIds.length > 0;
}

//The top level items grouped by their catalog type, in the order the types first appear, so a restricted question shows
//which type each branch starts from (as the mobile app does).
function groupByType(tree) {
  const groups = [];
  const groupsByTypeId = {};

  tree.forEach(branch => {
    const type = branch.type || {id: '', name: 'No type'};

    if (!groupsByTypeId[type.id]) {
      groupsByTypeId[type.id] = {type, tree: []};
      groups.push(groupsByTypeId[type.id]);
    }

    groupsByTypeId[type.id].tree.push(branch);
  });

  return groups;
}

//The pickable items together with the items above them, which stay in the tree so a pick is shown where it sits in the
//catalog, but can't be picked themselves.
function withParents(selectable, catalogById) {
  return Array.from(selectable).reduce((map, id) => {
    map[id] = catalogById[id];
    getParents(catalogById[id], catalogById).forEach(parent => {
      map[parent.id] = parent;
    });
    return map;
  }, {});
}

function CatalogTree({multiple, tree, value, selectable, onSelect}) {
  if (tree.length < 1) {
    return null;
  }

  return (
    <ul>
      {tree.map(branch => (
        <li key={branch.id}>
          {!selectable || selectable.has(branch.id) ? (
            <Button
              tertiary
              block
              center={false}
              active={multiple ? value.indexOf(branch.id) >= 0 : value === branch.id}
              onClick={() => onSelect(branch.id)}>
              {branch.name}
            </Button>
          ) : (
            <GroupLabel title="Only the items under this one can be picked">{branch.name}</GroupLabel>
          )}
          <CatalogTree multiple={multiple} tree={branch.children} value={value} selectable={selectable} onSelect={onSelect} />
        </li>
      ))}
    </ul>
  );
}

//sized like the tertiary buttons beside it so the tree's connecting lines still meet it
const GroupLabel = styled.div`
  padding: 0.4375rem 0.8125rem;
  line-height: 1.5;
  color: #747474;
  font-weight: 500;
  cursor: default;
`;

const TypeHeader = styled.div`
  padding: 0.5rem 0.8125rem 0;
  color: #747474;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const EmptyMessage = styled.p`
  margin: 0;
  padding: 1rem;
  color: #747474;
  text-align: center;
`;

const CatalogContainer = styled(Card)`
  height: 12rem;
  overflow: auto;

  ul, li {
    position: relative;
  }

  ul {
    list-style: none;
    padding-left: 2rem;
  }

  li::before, li::after {
    content: "";
    position: absolute;
    left: -1rem;
  }

  li::before {
    border-top: 1px solid #b4b4b4;
    top: 1rem;
    width: 1rem;
    height: 0;
  }

  li::after {
    border-left: 1px solid #b4b4b4;
    height: 100%;
    width: 0px;
    top: 0px;
  }

  ul > li:last-child::after {
    height: 1rem;
  }
`;

export default CatalogInput;